import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';
import './ReportViewer.css';

// Page Footer Component
const PageFooter = ({ pageNum }) => (
  <div className="page-footer">
    <div className="page-footer-left">
      <img src="/prisma-sigil.png" alt="The Prisma Global Logo" className="page-footer-sigil" />
      <span className="page-footer-company">THE PRISMA GLOBAL</span>
    </div>
    <div className="page-footer-center">
      MSME: UDYAM-MH-32-0255914
    </div>
    <div className="page-footer-right">
      © 2025 The Prisma Global | Page {pageNum}
    </div>
  </div>
);

const PremiumReportViewer = () => {
  // SEO Configuration
  const pageTitle = "Premium Impact Report - Full Analysis | The Prisma Global";
  const pageDescription = "Access The Prisma Global's comprehensive premium impact report. Detailed financial analysis, 5-year projections, investment thesis, and complete divisional breakdown. 18 pages of verified business data and metrics.";
  const pageKeywords = "premium business report, detailed impact analysis, investment report Prisma Global, paid business report, comprehensive business analysis, detailed financial report";
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Premium Impact Report - The Prisma Global",
    "description": "Comprehensive 18-page impact report with detailed analysis",
    "offers": {
      "@type": "Offer",
      "price": "999",
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock"
    },
    "brand": {
      "@type": "Organization",
      "name": "The Prisma Global"
    }
  };
  
  const handleDownloadPDF = () => {
    // Hide navbar for PDF
    const navbar = document.querySelector('.site-header');
    if (navbar) navbar.style.display = 'none';
    
    // Trigger print dialog
    window.print();
    
    // Restore navbar after print dialog closes
    setTimeout(() => {
      if (navbar) navbar.style.display = 'block';
    }, 100);
  };

  return (
    <div className="report-viewer">
      <SEO 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonical="/reports/premium"
        ogImage="https://theprismaglobal.com/hero-main.png"
        structuredData={structuredData}
      />
      
      {/* Navigation - Same as main site */}
      <Navbar />

      {/* Report Content */}
      <div className="report-container">
        {/* Page 1: Cover */}
        <div className="report-page cover-page premium-cover">
          <div className="premium-badge-cover">Premium Edition</div>
          <div className="cover-logo">
            <img src="/prisma-sigil.png" alt="Prisma Global Sigil" className="cover-sigil" />
          </div>
          <div className="cover-header">
            <div className="cover-badge">Detailed Impact Report 2025</div>
            <h1 className="cover-title">
              THE PRISMA<br/>
              <span className="highlight">GLOBAL</span>
            </h1>
            <p className="cover-subtitle">
              Comprehensive analysis of a purpose-driven enterprise building 
              world-class systems from Tier-2 India.
            </p>
          </div>
          
          <div className="cover-footer">
            <div className="cover-stats">
              <div className="cover-stat">
                <div className="cover-stat-value">$230K+</div>
                <div className="cover-stat-label">Revenue & Grants</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-value">₹12Cr+</div>
                <div className="cover-stat-label">Asset Base</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-value">300K+</div>
                <div className="cover-stat-label">Users Reached</div>
              </div>
              <div className="cover-stat">
                <div className="cover-stat-value">280+</div>
                <div className="cover-stat-label">Women Jobs Planned</div>
              </div>
            </div>
            
            <div className="cover-info">
              <div className="cover-company">THE PRISMA GLOBAL</div>
              <div className="cover-msme">MSME Registered | UDYAM-MH-32-0255914</div>
              <div className="cover-date">Solapur, Maharashtra • 2025</div>
            </div>
          </div>
        </div>

        {/* Page 2: Table of Contents */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">Table of Contents</h1>
          
          <div className="toc">
            <div className="toc-item"><span className="toc-title">1. Executive Summary</span><span className="toc-page">3</span></div>
            <div className="toc-item"><span className="toc-title">2. Company Overview & Vision</span><span className="toc-page">4</span></div>
            <div className="toc-item"><span className="toc-title">3. Founder Profile & Track Record</span><span className="toc-page">5</span></div>
            <div className="toc-item"><span className="toc-title">4. Business Divisions - Detailed Analysis</span><span className="toc-page">6-9</span></div>
            <div className="toc-item"><span className="toc-title">5. Case Study: Eco-Warriors™</span><span className="toc-page">10</span></div>
            <div className="toc-item"><span className="toc-title">6. Case Study: 2024 Election Campaign</span><span className="toc-page">11</span></div>
            <div className="toc-item"><span className="toc-title">7. Financial Highlights & Asset Valuation</span><span className="toc-page">12-13</span></div>
            <div className="toc-item"><span className="toc-title">8. Social Impact & SDG Alignment</span><span className="toc-page">14</span></div>
            <div className="toc-item"><span className="toc-title">9. Partners & Institutional Support</span><span className="toc-page">15</span></div>
            <div className="toc-item"><span className="toc-title">10. Investment Thesis & Market Opportunity</span><span className="toc-page">16</span></div>
            <div className="toc-item"><span className="toc-title">11. Future Roadmap</span><span className="toc-page">17</span></div>
            <div className="toc-item"><span className="toc-title">12. Contact Information</span><span className="toc-page">18</span></div>
          </div>
          
          <div className="highlight-box" style={{marginTop: '30px'}}>
            <p><strong>About This Report:</strong> This Premium Impact Report provides comprehensive analysis of The Prisma Global's operations, financials, impact metrics, and growth trajectory. Prepared for investors, partners, and institutional stakeholders seeking detailed due diligence information.</p>
          </div>
          <PageFooter pageNum={2} />
        </div>

        {/* Page 3: Executive Summary */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">1. Executive Summary</h1>
          
          <p className="report-text">The Prisma Global is a purpose-driven enterprise headquartered in Solapur, Maharashtra, founded in 2025 by Ms. Veda Lolge. Building on over a decade of entrepreneurial experience across India and Mauritius, the Group consolidates proven ventures into a multi-vertical structure spanning digital platforms, textile manufacturing, real estate, and strategic advisory.</p>
          
          <div className="highlight-box">
            <p>"The Prisma Global exists to empower local communities by building purpose-driven physical and digital systems that transform regional culture, education, and craftsmanship into globally scalable value—anchored in Solapur and designed for the world."</p>
          </div>
          
          <h2 className="subsection-title">Key Metrics at a Glance</h2>
          
          <div className="stats-grid">
            <div className="stat-box"><div className="value">$230K+</div><div className="label">Revenue & Grants</div></div>
            <div className="stat-box"><div className="value">₹12Cr+</div><div className="label">Asset Base</div></div>
            <div className="stat-box"><div className="value">300K+</div><div className="label">Users Reached</div></div>
            <div className="stat-box"><div className="value">10+</div><div className="label">Years Experience</div></div>
          </div>
          
          <div className="stats-grid">
            <div className="stat-box"><div className="value">4</div><div className="label">Business Divisions</div></div>
            <div className="stat-box"><div className="value">280+</div><div className="label">Women Jobs Planned</div></div>
            <div className="stat-box"><div className="value">8+</div><div className="label">Awards Won</div></div>
            <div className="stat-box"><div className="value">2</div><div className="label">Countries</div></div>
          </div>
          
          <h2 className="subsection-title">Strategic Positioning</h2>
          <p className="report-text">The Group positions itself as a bridge between Tier-2 India's untapped potential and global market opportunities. By building world-class systems from Solapur, Prisma Global demonstrates that talent, innovation, and value creation need not be concentrated in metropolitan centres.</p>
          
          <ul className="check-list">
            <li>Proven international revenue generation from Tier-2 base</li>
            <li>Asset-backed growth with ₹12Cr+ in land and real estate holdings</li>
            <li>Institutional validation (UNESCO, JICA, NCDC, MRIC)</li>
            <li>Women-centric employment model (280+ jobs planned)</li>
            <li>Multi-vertical diversification reducing single-sector risk</li>
          </ul>
          <PageFooter pageNum={3} />
        </div>

        {/* Page 4: Company Overview */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">2. Company Overview & Vision</h1>
          
          <table className="report-table">
            <tbody>
              <tr><td><strong>Full Name</strong></td><td>The Prisma Global</td></tr>
              <tr><td><strong>Founded</strong></td><td>2025</td></tr>
              <tr><td><strong>Headquarters</strong></td><td>Solapur, Maharashtra, India</td></tr>
              <tr><td><strong>Founder & CEO</strong></td><td>Ms. Veda Lolge</td></tr>
              <tr><td><strong>Website</strong></td><td>www.theprismaglobal.com</td></tr>
              <tr><td><strong>Sectors</strong></td><td>Digital Platforms, Textile Manufacturing, Real Estate, Strategic Advisory</td></tr>
            </tbody>
          </table>
          
          <h2 className="subsection-title">Mission Statement</h2>
          <div className="highlight-box">
            <p>The Prisma Global exists to empower local communities by building purpose-driven physical and digital systems that transform regional culture, education, and craftsmanship into globally scalable value—anchored in Solapur and designed for the world.</p>
          </div>
          
          <h2 className="subsection-title">Vision Statement</h2>
          <p className="report-text">Prisma Global envisions a future where Solapur becomes a globally connected, locally empowered economic hub—driven by circular systems, rural employment, cultural integrity, and innovation. Over the next decade, the Group aims to demonstrate that purpose-driven enterprises can deliver measurable social impact while remaining commercially viable and globally competitive.</p>
          
          <h2 className="subsection-title">Key Milestones Timeline</h2>
          
          <div className="timeline">
            <div className="timeline-item"><div className="timeline-year">2014</div><div className="timeline-text">Founded Panda & Wolf Advertising Agency in Pune</div></div>
            <div className="timeline-item"><div className="timeline-year">2017</div><div className="timeline-text">Expanded Panda & Wolf to Mauritius</div></div>
            <div className="timeline-item"><div className="timeline-year">2018</div><div className="timeline-text">Launched Discover Mauritius™ cultural travel app</div></div>
            <div className="timeline-item"><div className="timeline-year">2019</div><div className="timeline-text">Eco-Warriors™ launched with UNESCO partnership; Won Seedstars Competition</div></div>
            <div className="timeline-item"><div className="timeline-year">2020</div><div className="timeline-text">JICA Grant (USD 30,000) & MRIC Research Grant (MUR 1M)</div></div>
            <div className="timeline-item"><div className="timeline-year">2021</div><div className="timeline-text">300K users on Discover Mauritius™; UNESCO Patronage</div></div>
            <div className="timeline-item"><div className="timeline-year">2023</div><div className="timeline-text">Secured ₹2.62Cr NCDC funding for textile cooperatives</div></div>
            <div className="timeline-item"><div className="timeline-year">2024</div><div className="timeline-text">Winning election campaign for Maharashtra Revenue Minister</div></div>
            <div className="timeline-item"><div className="timeline-year">2025</div><div className="timeline-text">Founded The Prisma Global, consolidating all ventures</div></div>
          </div>
          <PageFooter pageNum={4} />
        </div>

        {/* Page 5: Founder Profile */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">3. Founder Profile & Track Record</h1>
          
          <table className="report-table">
            <tbody>
              <tr><td><strong>Full Name</strong></td><td>Ms. Veda Lolge</td></tr>
              <tr><td><strong>Title</strong></td><td>CEO & Founder</td></tr>
              <tr><td><strong>Education</strong></td><td>B.F.A (Fine & Applied Arts)</td></tr>
              <tr><td><strong>Experience</strong></td><td>10+ years in entrepreneurship across India and Mauritius</td></tr>
              <tr><td><strong>Previous Ventures</strong></td><td>Panda & Wolf Advertising Agency (2014-2024)</td></tr>
            </tbody>
          </table>
          
          <h2 className="subsection-title">Founder's Message</h2>
          <div className="highlight-box">
            <p>"I built Prisma Global because I have seen firsthand how much potential exists in local communities—and how often that potential is overlooked or extracted without long-term benefit. This company was created to ensure that knowledge, culture, and skilled work from places like Solapur are valued, protected, and scaled with dignity."</p>
          </div>
          
          <h2 className="subsection-title">Awards & Recognition</h2>
          
          <div className="awards-list">
            <div className="award-item"><div className="award-year">2022</div><div className="award-text">Africa's Women Leader — World Women Leadership Congress</div></div>
            <div className="award-item"><div className="award-year">2021</div><div className="award-text">UNESCO Patronage for Eco-Warriors™</div></div>
            <div className="award-item"><div className="award-year">2021</div><div className="award-text">Industry 5.0 Panelist — Team Synthesis Group</div></div>
            <div className="award-item"><div className="award-year">2020</div><div className="award-text">NINJA Competition Winner (USD 30K) — JICA</div></div>
            <div className="award-item"><div className="award-year">2020</div><div className="award-text">Best Sustainable EdTech Award — Women in Tech Africa</div></div>
            <div className="award-item"><div className="award-year">2020</div><div className="award-text">MRIC Research Grant (MUR 1M) — Mauritius Research Council</div></div>
            <div className="award-item"><div className="award-year">2019</div><div className="award-text">Female Role Model of the Year — Global Startup Awards</div></div>
            <div className="award-item"><div className="award-year">2019</div><div className="award-text">Digital Women's Award — She The People & Google India</div></div>
          </div>
          <PageFooter pageNum={5} />
        </div>

        {/* Page 6: Prisma Digital */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">4. Business Divisions</h1>
          
          <div className="division-section">
            <div className="division-header">
              <div className="division-icon">🖥️</div>
              <div className="division-title">PRISMA DIGITAL</div>
            </div>
            
            <p className="report-text">Prisma Digital designs and operates digital platforms, media systems, and technology-enabled products that enable cultural discovery, education, and brand-led community engagement, with a focus on scalable and white-label-ready solutions.</p>
            
            <h4>Key Products & Services</h4>
            <ul className="check-list">
              <li>Discover Mauritius™ – Cultural discovery & tourism SaaS platform (historical)</li>
              <li>Eco-Warriors™ – Environmental EdTech game (UNESCO patronage)</li>
              <li>Digital media production, platform branding, and technology consulting</li>
              <li>White-label and B2B platform deployment services</li>
            </ul>
            
            <h4>Target Clients</h4>
            <ul className="check-list">
              <li>Tourism boards, cultural institutions, and city governments</li>
              <li>Educational institutions and NGOs</li>
              <li>Brands seeking culture-led digital engagement</li>
              <li>International partners (tested in Mauritius)</li>
            </ul>
            
            <h4>Revenue & Impact</h4>
            <div className="stats-grid stats-grid-3">
              <div className="stat-box"><div className="value">$200K+</div><div className="label">Commercial Revenue</div></div>
              <div className="stat-box"><div className="value">$30K</div><div className="label">JICA Grant</div></div>
              <div className="stat-box"><div className="value">300K+</div><div className="label">Users Reached</div></div>
            </div>
          </div>
          <PageFooter pageNum={6} />
        </div>

        {/* Page 7: PrismaTex */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <div className="division-section">
            <div className="division-header">
              <div className="division-icon">🧵</div>
              <div className="division-title">PRISMATEX — Textile Manufacturing</div>
            </div>
            
            <p className="report-text">PrismaTex is Prisma Global's textile and apparel vertical, rooted in Solapur's weaving and textile heritage. The division focuses on value-added manufacturing that combines traditional craftsmanship with contemporary design and global branding.</p>
            
            <h4>Product Categories</h4>
            <ul className="check-list">
              <li>Apparel and limited-edition garment lines</li>
              <li>Solapur chaddar–inspired textiles and lifestyle products</li>
              <li>Small-batch, provenance-based collections (Edition-led drops)</li>
            </ul>
            
            <h4>Asset Base</h4>
            <div className="financial-highlight">
              <div className="financial-card">
                <h4>Kumbhari Industrial Land</h4>
                <div className="asset-item"><span className="asset-label">Location</span><span className="asset-value">Kumbhari, Solapur</span></div>
                <div className="asset-item"><span className="asset-label">Area</span><span className="asset-value">20,000 sq. ft. (2 parcels)</span></div>
                <div className="asset-item"><span className="asset-label">Valuation</span><span className="asset-value">₹2 Crore</span></div>
                <div className="asset-item"><span className="asset-label">Purpose</span><span className="asset-value">Factory Construction</span></div>
              </div>
              <div className="financial-card">
                <h4>NCDC Support</h4>
                <div className="asset-item"><span className="asset-label">Grant Status</span><span className="asset-value">Sanctioned (2024)</span></div>
                <div className="asset-item"><span className="asset-label">Focus</span><span className="asset-value">Women Employment</span></div>
                <div className="asset-item"><span className="asset-label">Jobs Planned</span><span className="asset-value">280+ Women</span></div>
                <div className="asset-item"><span className="asset-label">Model</span><span className="asset-value">Cooperative-based</span></div>
              </div>
            </div>
          </div>
          <PageFooter pageNum={7} />
        </div>

        {/* Page 8: Prisma Terra & Policy */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <div className="division-section">
            <div className="division-header">
              <div className="division-icon">🏗️</div>
              <div className="division-title">PRISMA TERRA — Real Estate</div>
            </div>
            
            <p className="report-text">Prisma Terra manages land aggregation, plotting, and real estate advisory services, with a focus on long-term, community-aligned development rather than speculative real estate activity.</p>
            
            <h4>Flagship Project: Nakshatra Villas</h4>
            <div className="financial-card">
              <div className="asset-item"><span className="asset-label">Project Name</span><span className="asset-value">Nakshatra Villas (formerly La Casa)</span></div>
              <div className="asset-item"><span className="asset-label">Land Area</span><span className="asset-value">Approximately 50 acres</span></div>
              <div className="asset-item"><span className="asset-label">Valuation</span><span className="asset-value">₹10 Crore (approx.)</span></div>
              <div className="asset-item"><span className="asset-label">Type</span><span className="asset-value">Land aggregation, plotting & villa development</span></div>
            </div>
          </div>
          
          <div className="division-section">
            <div className="division-header">
              <div className="division-icon">📋</div>
              <div className="division-title">PRISMA POLICY — Strategic Advisory</div>
            </div>
            
            <p className="report-text">Prisma Policy provides strategic advisory, campaign management support, and institutional consulting for public leaders, government-linked entities, and mission-driven organizations.</p>
            
            <h4>Services</h4>
            <ul className="check-list">
              <li>Voter outreach and grassroots engagement systems</li>
              <li>Digital communication and media strategy</li>
              <li>Campaign design and execution</li>
              <li>Institutional consulting for public impact</li>
            </ul>
          </div>
          <PageFooter pageNum={8} />
        </div>

        {/* Page 9: Case Study - Eco Warriors */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">5. Case Study: Eco-Warriors™</h1>
          
          <div className="case-study-box">
            <h3>🎮 First UNESCO-Patronaged Educational Mobile Game in Africa</h3>
            <p>Eco-Warriors™ is an educational mobile game that teaches children aged 6-11 about waste sorting, recycling, and environmental responsibility through gamification. Players learn history and geography while collecting waste, sorting recyclables, and protecting endemic animals.</p>
            
            <div className="case-study-stats">
              <div className="case-study-stat"><div className="value">UNESCO</div><div className="label">Patronage</div></div>
              <div className="case-study-stat"><div className="value">300+</div><div className="label">Schools Partnered</div></div>
              <div className="case-study-stat"><div className="value">MUR 1.75M</div><div className="label">Funding Secured</div></div>
            </div>
          </div>
          
          <h2 className="subsection-title">Recognition & Funding</h2>
          <ul className="check-list">
            <li>UNESCO Patronage (2021) — First for an educational mobile game in Africa</li>
            <li>JICA NINJA Grant — USD 30,000 for product development</li>
            <li>MRIC Research Grant — MUR 1,000,000 for R&D</li>
            <li>Seedstars Competition Winner — International startup recognition</li>
            <li>Best Sustainable EdTech — Women in Tech Africa Awards</li>
          </ul>
          
          <h2 className="subsection-title">Impact Metrics</h2>
          <div className="stats-grid">
            <div className="stat-box"><div className="value">300+</div><div className="label">Schools</div></div>
            <div className="stat-box"><div className="value">50K+</div><div className="label">Students</div></div>
            <div className="stat-box"><div className="value">15+</div><div className="label">Partners</div></div>
            <div className="stat-box"><div className="value">5</div><div className="label">Awards</div></div>
          </div>
          <PageFooter pageNum={9} />
        </div>

        {/* Page 10: Case Study - Election Campaign */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">6. Case Study: 2024 Election Campaign</h1>
          
          <div className="case-study-box">
            <h3>🏆 Winning Campaign for Maharashtra Revenue Minister</h3>
            <p>Prisma Policy led a comprehensive grassroots and digital campaign for Chandrashekhar Bawankule, resulting in a successful election to the position of Maharashtra Revenue Minister in 2024.</p>
            
            <div className="case-study-stats">
              <div className="case-study-stat"><div className="value">✓</div><div className="label">Victory</div></div>
              <div className="case-study-stat"><div className="value">Full</div><div className="label">360° Campaign</div></div>
              <div className="case-study-stat"><div className="value">State</div><div className="label">Level Impact</div></div>
            </div>
          </div>
          
          <h2 className="subsection-title">Campaign Elements</h2>
          <ul className="check-list">
            <li>Grassroots voter outreach and engagement</li>
            <li>Digital communication strategy and social media management</li>
            <li>Media relations and press coordination</li>
            <li>Event management and rally organization</li>
            <li>Data-driven targeting and messaging</li>
          </ul>
          
          <h2 className="subsection-title">Outcome</h2>
          <p className="report-text">The campaign resulted in a decisive victory, establishing Prisma Policy's credibility in political consulting and strategic advisory at the state government level. This success demonstrates the division's capability to manage high-stakes, public-facing campaigns with measurable outcomes.</p>
          <PageFooter pageNum={10} />
        </div>

        {/* Page 11: Financial Highlights */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">7. Financial Highlights & Asset Valuation</h1>
          
          <h2 className="subsection-title">Revenue & Funding Summary</h2>
          
          <div className="stats-grid">
            <div className="stat-box"><div className="value">$200K+</div><div className="label">Commercial Revenue</div></div>
            <div className="stat-box"><div className="value">$30K</div><div className="label">JICA Grant</div></div>
            <div className="stat-box"><div className="value">₹2.62Cr</div><div className="label">NCDC Funding</div></div>
            <div className="stat-box"><div className="value">MUR 1M</div><div className="label">MRIC Grant</div></div>
          </div>
          
          <h2 className="subsection-title">Asset Portfolio</h2>
          
          <div className="financial-highlight">
            <div className="financial-card">
              <h4>Real Estate Assets</h4>
              <div className="asset-item"><span className="asset-label">Nakshatra Villas Land</span><span className="asset-value">₹10 Crore</span></div>
              <div className="asset-item"><span className="asset-label">Kumbhari Industrial Land</span><span className="asset-value">₹2 Crore</span></div>
              <div className="asset-item"><span className="asset-label"><strong>Total Real Estate</strong></span><span className="asset-value"><strong>₹12+ Crore</strong></span></div>
            </div>
            <div className="financial-card">
              <h4>Digital Assets</h4>
              <div className="asset-item"><span className="asset-label">Discover Mauritius™ Platform</span><span className="asset-value">300K+ Users</span></div>
              <div className="asset-item"><span className="asset-label">Eco-Warriors™ IP</span><span className="asset-value">UNESCO Patronage</span></div>
              <div className="asset-item"><span className="asset-label">Brand Portfolio</span><span className="asset-value">4 Divisions</span></div>
            </div>
          </div>
          
          <h2 className="subsection-title">Investment Highlights</h2>
          <ul className="check-list">
            <li>Asset-backed business model with ₹12Cr+ in tangible assets</li>
            <li>Multiple revenue streams across 4 diversified divisions</li>
            <li>Proven track record of securing institutional funding</li>
            <li>Low overhead through Tier-2 headquarters location</li>
            <li>Scalable digital platforms with demonstrated user traction</li>
          </ul>
          <PageFooter pageNum={11} />
        </div>

        {/* Page 12: Social Impact */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">8. Social Impact & SDG Alignment</h1>
          
          <p className="report-text">The Prisma Global's operations align with multiple United Nations Sustainable Development Goals, demonstrating commitment to measurable social impact alongside commercial viability.</p>
          
          <h2 className="subsection-title">SDG Alignment</h2>
          
          <div className="sdg-grid">
            <div className="sdg-item"><div className="sdg-number">4</div><div className="sdg-label">Quality Education</div></div>
            <div className="sdg-item"><div className="sdg-number">5</div><div className="sdg-label">Gender Equality</div></div>
            <div className="sdg-item"><div className="sdg-number">8</div><div className="sdg-label">Decent Work</div></div>
            <div className="sdg-item"><div className="sdg-number">9</div><div className="sdg-label">Industry & Innovation</div></div>
            <div className="sdg-item"><div className="sdg-number">11</div><div className="sdg-label">Sustainable Cities</div></div>
            <div className="sdg-item"><div className="sdg-number">12</div><div className="sdg-label">Responsible Consumption</div></div>
            <div className="sdg-item"><div className="sdg-number">13</div><div className="sdg-label">Climate Action</div></div>
            <div className="sdg-item"><div className="sdg-number">17</div><div className="sdg-label">Partnerships</div></div>
          </div>
          
          <h2 className="subsection-title">Impact Metrics</h2>
          <div className="stats-grid">
            <div className="stat-box"><div className="value">280+</div><div className="label">Women Jobs Planned</div></div>
            <div className="stat-box"><div className="value">300+</div><div className="label">Schools Reached</div></div>
            <div className="stat-box"><div className="value">300K+</div><div className="label">Users Engaged</div></div>
            <div className="stat-box"><div className="value">50+</div><div className="label">Local Artisans</div></div>
          </div>
          <PageFooter pageNum={12} />
        </div>

        {/* Page 13: Partners */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">9. Partners & Institutional Support</h1>
          
          <h2 className="subsection-title">Institutional Partners</h2>
          
          <div className="partner-grid">
            <div className="partner-card"><div className="partner-name">UNESCO</div><div className="partner-type">Patronage Partner</div></div>
            <div className="partner-card"><div className="partner-name">JICA</div><div className="partner-type">Grant Partner</div></div>
            <div className="partner-card"><div className="partner-name">NCDC</div><div className="partner-type">Funding Partner</div></div>
            <div className="partner-card"><div className="partner-name">MRIC</div><div className="partner-type">Research Partner</div></div>
            <div className="partner-card"><div className="partner-name">Seedstars</div><div className="partner-type">Accelerator</div></div>
            <div className="partner-card"><div className="partner-name">Google India</div><div className="partner-type">Recognition Partner</div></div>
          </div>
          
          <h2 className="subsection-title">Commercial Partners</h2>
          <ul className="check-list">
            <li>Grand Baie La Croisette Mall, Mauritius</li>
            <li>Cascavelle Mall, Mauritius</li>
            <li>Polytechnics Mauritius University</li>
            <li>100+ Local Vendors and SMEs</li>
          </ul>
          <PageFooter pageNum={13} />
        </div>

        {/* Page 14: Investment Thesis */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">10. Investment Thesis & Market Opportunity</h1>
          
          <div className="investment-box">
            <h2>Why Tier-2 India?</h2>
            <p>Tier-2 cities represent the next frontier of India's growth story, with lower costs, untapped talent pools, and rapidly improving infrastructure.</p>
          </div>
          
          <h2 className="subsection-title">Investment Highlights</h2>
          <ul className="check-list">
            <li><strong>Asset-Backed:</strong> ₹12Cr+ in tangible real estate assets</li>
            <li><strong>Diversified:</strong> 4 business divisions reducing single-sector risk</li>
            <li><strong>Proven Revenue:</strong> $230K+ in revenue and grants secured</li>
            <li><strong>Institutional Validation:</strong> UNESCO, JICA, NCDC backing</li>
            <li><strong>Scalable Digital:</strong> 300K+ users on digital platforms</li>
            <li><strong>Impact-Driven:</strong> 280+ women jobs planned</li>
          </ul>
          
          <h2 className="subsection-title">Market Opportunity</h2>
          <p className="report-text">India's Tier-2 and Tier-3 cities are experiencing rapid growth in digital adoption, consumer spending, and infrastructure development. Prisma Global is positioned to capture this opportunity through its multi-vertical approach spanning digital, textile, real estate, and advisory services.</p>
          <PageFooter pageNum={14} />
        </div>

        {/* Page 15: Future Roadmap */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-sigil.png" alt="" className="page-header-sigil" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">11. Future Roadmap</h1>
          
          <h2 className="subsection-title">2025-2026 Priorities</h2>
          <ul className="check-list">
            <li>Launch PrismaTex manufacturing facility in Kumbhari</li>
            <li>Expand Discover Solapur™ to 3+ new markets</li>
            <li>Complete Nakshatra Villas Phase 1 development</li>
            <li>Grow institutional advisory portfolio</li>
          </ul>
          
          <h2 className="subsection-title">2027-2030 Vision</h2>
          <ul className="check-list">
            <li>280+ women employed in PrismaTex operations</li>
            <li>International expansion of digital platforms</li>
            <li>₹50Cr+ asset base through real estate growth</li>
            <li>Establish Solapur as a model Tier-2 innovation hub</li>
          </ul>
          
          <h2 className="subsection-title">Long-Term Impact Goals</h2>
          <p className="report-text">By 2030, Prisma Global aims to demonstrate that purpose-driven enterprises headquartered in Tier-2 India can achieve global scale while delivering measurable social impact. The company seeks to create a replicable model for community-aligned, asset-backed growth that preserves local culture and creates sustainable livelihoods.</p>
          <PageFooter pageNum={15} />
        </div>

        {/* Page 16: Contact */}
        <div className="report-page">
          <div className="page-header">
            <img src="/prisma-logotype.jpg" alt="Prisma Global" className="page-header-logo-img" />
            <div className="page-header-title">Premium Impact Report 2025</div>
          </div>
          
          <h1 className="section-title">12. Contact Information</h1>
          
          <div className="contact-grid">
            <div className="contact-item">
              <div className="contact-label">Email</div>
              <div className="contact-value">info@theprismaglobal.com</div>
            </div>
            <div className="contact-item">
              <div className="contact-label">Phone</div>
              <div className="contact-value">+91 9356 107 460</div>
            </div>
            <div className="contact-item">
              <div className="contact-label">Website</div>
              <div className="contact-value">www.theprismaglobal.com</div>
            </div>
            <div className="contact-item">
              <div className="contact-label">Location</div>
              <div className="contact-value">Solapur, Maharashtra, India</div>
            </div>
          </div>
          
          <div className="highlight-box" style={{marginTop: '40px'}}>
            <p><strong>Investment Inquiries:</strong> For investment opportunities, partnership discussions, or institutional collaborations, please contact us at info@theprismaglobal.com with the subject line "Investment Inquiry" or "Partnership Inquiry."</p>
          </div>
          
          <div className="report-footer-text" style={{marginTop: '60px'}}>
            <img src="/prisma-sigil.png" alt="Prisma Global Sigil" className="footer-sigil" />
            <p><strong>THE PRISMA GLOBAL</strong></p>
            <p>Where Light Becomes Legacy</p>
            <p className="footer-msme-report">MSME Registered | UDYAM-MH-32-0255914</p>
            <p>© 2025 The Prisma Global. All Rights Reserved.</p>
            <p style={{marginTop: '20px', fontSize: '10px', color: '#999'}}>This document is confidential and intended for the recipient only. Reproduction or distribution without written consent is prohibited.</p>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="download-section">
        <button onClick={handleDownloadPDF} className="download-btn">
          📥 Download as PDF
        </button>
        <p className="download-hint">Click to download the report as PDF</p>
      </div>
    </div>
  );
};

export default PremiumReportViewer;
